/**
 * Feature flags — org-scoped UI gates.
 *
 * Usage:
 *   import { isFeatureEnabled } from '../utils/featureFlags';
 *   if (isFeatureEnabled('AGENT_ATLAS')) { ... }
 *
 * Add a flag by dropping a new entry into FLAGS. `allowedOrgIds` uses string
 * comparison, so IDs like '3' and 3 both work. Use `'*'` to enable everywhere.
 */

export const FLAGS = {
  AGENT_PAGE_V2: {
    label: 'Saf3AI Agent page (redesign)',
    description: 'The 2026-10 Saf3AI Agent page at /ai-explorer: conversation history pane, streamed answers with Stop, follow-ups, feedback, dashboards, slash commands, time window. Off falls back to the previous page (pages/ai-explorer/legacy.jsx).',
    allowedOrgIds: ['*'],
  },
  ALERTS_AI_HOME: {
    label: 'Alert Configuration, AI-first',
    description: 'Alert Configuration (/admin/alert-configuration) opens on the AI-first home: an ask box that opens the Saf3AI Agent, what Saf3AI suggests (GET /alerts/suggestions), and every rule in plain words with on/off and Customise (GET /alerts/rules, the rule editor in a slide panel at ?rule=<id>). The routing tables stay at ?view=advanced (and any ?tab= link). The agent\'s create/update alert rule approvals open as the same editor either way. Off restores the tabs as the default view.',
    allowedOrgIds: ['*'],
  },
  AGENT_CANVAS: {
    label: 'Saf3AI Agent canvas',
    description: 'A canvas beside the agent chat (/ai-explorer?canvas=<id>): one-click Pin on every answer, highlight a sentence to pin it as an insight, free move/resize on a 12-column grid. Pins from the slide-in panel on any page go to the same active canvas. A canvas is an AI dashboard, so it also opens at /ai-explorer/dashboards/:id. Off restores "Save to dashboard". Needs the AI microservice with widget layout/message_id/kind and dashboards?conversation_id=.',
    allowedOrgIds: ['*'],
  },
  AGENT_ATLAS: {
    label: 'Agent Atlas',
    description: 'Retired as a gate (2026-10-06): Agent Atlas is a core module, so no route or nav reads this. Kept defined for the flag tests and any external reference; licensing lives in src/licensing.',
    // Open for client demo — restrict again after the demo if needed.
    allowedOrgIds: ['*'],
  },
  AGENT_ONBOARDING: {
    label: 'Agent onboarding',
    description: 'Onboarding card and steps on the agent page: owning team, named owner, purpose, criticality. Open everywhere like the other Atlas pages (an org-scoped gate hides it from super admins, who carry no organization_id); flip to [] to switch the card off. Needs the agent-atlas build with /onboarding.',
    allowedOrgIds: ['*'],
  },
  AI_GATEWAY: {
    label: 'AI Gateway',
    description: 'Retired as a gate (2026-10-06): the AI Gateway pages are the `ai_gateway` module, locked by licence (src/licensing), not by this flag.',
    allowedOrgIds: ['*'],
  },
  SHADOW_AI: {
    label: 'Shadow AI (consolidated)',
    description: 'Retired as a gate (2026-10-06): Endpoint Security is the `endpoint` module, locked by licence (src/licensing), not by this flag.',
    allowedOrgIds: ['*'],
  },
  MDM: {
    label: 'Managed Devices (MDM)',
    description: 'No longer gates routes or nav (2026-10-06): the MDM pages are the `mdm` module, locked by licence. Still read by the Integrations page to show the Jamf / Intune connectors.',
    allowedOrgIds: ['*'],
  },
  EDR: {
    label: 'EDR (CrowdStrike + Defender)',
    description: 'No longer gates routes or nav (2026-10-06): the EDR pages are the `edr` module, locked by licence. Still read by the Integrations page to show the CrowdStrike / Defender connectors.',
    allowedOrgIds: ['*'],
  },
  GEMINI_ENTERPRISE_POLICY: {
    label: 'Gemini Enterprise policy',
    description: 'Gemini Enterprise customer-policy reconciliation and violations.',
    // Enabled globally for the Gemini Enterprise policy and violations pages.
    allowedOrgIds: ['*'],
  },
  RECOMMENDATIONS: {
    label: 'Inbox',
    description: 'The Atlas Inbox (/agent-atlas/inbox) — rule-engine recommendations served by saf3ai-recommendation-microservice. Gated to org 3 while the service is pre-GA: the Rules admin page carries the same restriction, and an org without the service deployed would see an unavailable banner instead of a worklist.',
    allowedOrgIds: ['3'],
  },
  I18N: {
    label: 'Multi-language UI',
    description: 'Locale switcher and translated catalogs. Gated while the string sweep is in progress — only modules that have been migrated render translated, so this stays restricted until coverage is broad enough to avoid mixed-language pages.',
    allowedOrgIds: ['3'],
  },
  GCS_OBJECT_STORAGE: {
    label: 'GCS object storage',
    description: 'Private GCS bucket log ingestion (approved GCS slice). The configured card is derived from the persisted gcp_audit object-storage mode — bucket/prefix/format/sources from redacted config_metadata, lifecycle via the gcp_audit credential endpoints.',
    allowedOrgIds: ['*'],
  },
  CUSTOM_AGENTS_DASHBOARD: {
    label: 'Custom Agents Dashboard',
    description: 'Retires the legacy Custom Agents /dashboard page: the URL forwards to the Custom Agents Dashboard (/custom-agents/dashboard, the module overview) with its query and hash intact. This is the rollback switch — set allowedOrgIds to [] to render the legacy page again. Its code stays deployable until its unique panels have migrated (CUSTOM_AGENTS_GEMINI_TRANSITION_REPORT.md §7.5.1).',
    allowedOrgIds: ['*'],
  },
  AGENT_USER_360: {
    label: 'Agent 360 and User 360',
    description: 'The redesigned agent page (/agent-atlas/agents/:agentId) and user page (/agent-atlas/users/:email): coverage strip, KPI tiles, section navigation, recommendations, onboarding, behaviour, risk, guardrails, controls, assurance, cost and activity sections with deep links into each module. Module mounts of these pages (Gemini, AI Providers, Cloud, Custom Agents, Endpoint) inherit it. Off renders the current pages unchanged. On for every organisation since 2026-10-17.',
    allowedOrgIds: ['*'],
  },
  COMPLIANCE_ASSURANCE: {
    label: 'Compliance assurance',
    description: 'The compliance platform built on the SAF3AI control catalogue: Readiness, Controls and Frameworks tabs on Atlas Compliance (the current page becomes the Runtime tab), import and export of compliance packages, role and EU AI Act tier on Responsible AI Systems, the Agent 360 compliance section, generated documents and auditor access on Audit & documents. Off renders the current pages unchanged. On for the development organisation first.',
    allowedOrgIds: ['3'],
  },
  CUSTOM_AGENTS_ROUTES: {
    label: 'Custom Agents canonical routes',
    description: 'Serves the namespaced /custom-agents/* routes (CUSTOM_AGENTS_GEMINI_TRANSITION_REPORT.md §11). Canonical Custom pages are enabled for every organisation; unavailable read APIs render their structured empty states until data arrives. The Dashboard (/custom-agents/dashboard) is routed outside this gate and renders either way.',
    allowedOrgIds: ['*'],
  },
  CLOUD_BILLING: {
    label: 'Cloud billing integrations',
    description: 'Integrations › Billing (GCP, AWS and Azure bills). Hidden for every organisation, 2026-11-01: the billing ingestors are built (branch feat/cloud-billing) but not deployed, so a saved connector would collect nothing. Add organisation ids, or "*", to show it.',
    allowedOrgIds: [],
  },
  PILLAR_NAV: {
    label: 'Pillar navigation',
    description: 'Phase 1 of the platform nav restructure (platform-restructure-spec v2): offers a "New navigation" switch in the account menu that redraws the Agent Atlas section as Home, Inbox and the Discovery / Assessment / Security / FinOps & Adoption / Governance pillars, and moves Management into the account menu. Product module sections are unchanged and no URL changes. Off, or switched off by the user, renders the classic sidebar unchanged. Development organisation and the test organisations 27, 28 and 29 while it is built.',
    allowedOrgIds: ['3', '27', '28', '29'],
  },
};

/** Read the current user from localStorage (mirrors ProtectedRoute's logic). */
export function readCurrentUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Handle wrapped shape `{ user: {...} }` that some login flows emit.
    return parsed?.user && typeof parsed.user === 'object'
      ? { ...parsed.user, ...parsed }
      : parsed;
  } catch {
    return null;
  }
}

function normalizeOrgId(user) {
  const v = user?.organization_id ?? user?.org_id ?? user?.organizationId ?? '';
  return String(v);
}

/** Is this feature flag enabled for the given user (or current user)? */
export function isFeatureEnabled(flagKey, user = readCurrentUser()) {
  const flag = FLAGS[flagKey];
  if (!flag) return false;
  if (!flag.allowedOrgIds || flag.allowedOrgIds.length === 0) return false;
  if (flag.allowedOrgIds.includes('*')) return true;
  const orgId = normalizeOrgId(user);
  return flag.allowedOrgIds.map(String).includes(orgId);
}
